import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { type RootState } from '@/platform/store/types';
import {
  type AgentSourceEntity,
  type AgentSourceErrorCode,
} from '../models/agent-source.model';
import type { WithSlice } from '@reduxjs/toolkit';

export type AgentSourceListStatus = 'idle' | 'loading' | 'success' | 'error';

export interface AgentSourceSliceError {
  code: AgentSourceErrorCode;
  detail: string;
}

export interface AgentSourceListSlot {
  items: AgentSourceEntity[];
  status: AgentSourceListStatus;
  error: AgentSourceSliceError | null;
  /** Source IDs currently uploading (optimistic). */
  uploadingNames: string[];
}

export interface AgentSourceState {
  /** Keyed by `agent_config_id`. */
  byAgent: Record<string, AgentSourceListSlot>;
}

const emptySlot = (): AgentSourceListSlot => ({
  items: [],
  status: 'idle',
  error: null,
  uploadingNames: [],
});

const initialState: AgentSourceState = { byAgent: {} };

export const agentSourceSlice = createSlice({
  name: 'agentSource',
  initialState,
  reducers: {
    setListLoading: (state, action: PayloadAction<{ agentId: string }>) => {
      const slot = state.byAgent[action.payload.agentId] ?? emptySlot();
      slot.status = 'loading';
      slot.error = null;
      state.byAgent[action.payload.agentId] = slot;
    },
    setListData: (
      state,
      action: PayloadAction<{ agentId: string; items: AgentSourceEntity[] }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId] ?? emptySlot();
      slot.items = action.payload.items;
      slot.status = 'success';
      slot.error = null;
      state.byAgent[action.payload.agentId] = slot;
    },
    setListError: (
      state,
      action: PayloadAction<{ agentId: string; error: AgentSourceSliceError }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId] ?? emptySlot();
      slot.status = 'error';
      slot.error = action.payload.error;
      state.byAgent[action.payload.agentId] = slot;
    },
    upsertSource: (
      state,
      action: PayloadAction<{ agentId: string; item: AgentSourceEntity }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId] ?? emptySlot();
      const idx = slot.items.findIndex((s) => s._id === action.payload.item._id);
      if (idx >= 0) slot.items[idx] = action.payload.item;
      else slot.items.unshift(action.payload.item);
      state.byAgent[action.payload.agentId] = slot;
    },
    removeSource: (
      state,
      action: PayloadAction<{ agentId: string; sourceId: string }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId];
      if (!slot) return;
      slot.items = slot.items.filter((s) => s._id !== action.payload.sourceId);
    },
    addUploading: (
      state,
      action: PayloadAction<{ agentId: string; name: string }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId] ?? emptySlot();
      if (!slot.uploadingNames.includes(action.payload.name)) {
        slot.uploadingNames.push(action.payload.name);
      }
      state.byAgent[action.payload.agentId] = slot;
    },
    removeUploading: (
      state,
      action: PayloadAction<{ agentId: string; name: string }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId];
      if (!slot) return;
      slot.uploadingNames = slot.uploadingNames.filter(
        (n) => n !== action.payload.name,
      );
    },
    /** Merges a partial WebSocket event payload into the matching source item. */
    patchSourceFields: (
      state,
      action: PayloadAction<{
        agentId: string;
        sourceId: string;
        fields: Partial<AgentSourceEntity>;
      }>,
    ) => {
      const slot = state.byAgent[action.payload.agentId];
      if (!slot) return;
      const idx = slot.items.findIndex((s) => s._id === action.payload.sourceId);
      if (idx < 0) return;
      slot.items[idx] = { ...slot.items[idx], ...action.payload.fields };
    },
  },
});

export const {
  setListLoading,
  setListData,
  setListError,
  upsertSource,
  removeSource,
  addUploading,
  removeUploading,
  patchSourceFields,
} = agentSourceSlice.actions;

const EMPTY_SLOT: AgentSourceListSlot = emptySlot();

const selectSlice = (state: RootState): AgentSourceState =>
  (state.agentSource as AgentSourceState | undefined) ?? initialState;

export const makeSelectSourcesSlot = (agentId: string) =>
  createSelector(
    [selectSlice],
    (slice) => slice.byAgent[agentId] ?? EMPTY_SLOT,
  );

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof agentSourceSlice> {}
}
